import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingUp as TrendingIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  Accessibility as AccessibilityIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  MonetizationOn as MoneyIcon
} from '@mui/icons-material';

const AuditGenerator = ({ business, websiteAnalysis }) => {
  const [expandedSection, setExpandedSection] = useState('critical');
  const [copiedText, setCopiedText] = useState('');

  if (!business || !websiteAnalysis) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Analysez d'abord un site web pour générer l'audit
        </Typography>
      </Box>
    );
  }

  // Génération des axes d'amélioration
  const generateImprovementAxes = () => {
    const axes = [];
    
    // Axe 1: Performance et Vitesse
    if (websiteAnalysis.performance.performanceScore < 70) {
      axes.push({
        id: 'performance',
        title: '🚀 Performance et Vitesse',
        priority: 'CRITIQUE',
        impact: 'Élevé',
        description: 'Votre site met plus de 3 secondes à se charger, ce qui fait fuir 53% des visiteurs',
        problems: [
          'Temps de chargement trop lent (> 3 secondes)',
          'Images non optimisées',
          'Pas de mise en cache',
          'Code non minifié'
        ],
        businessImpact: 'Perte de 40% des conversions potentielles',
        solution: 'Optimisation complète pour un chargement en moins de 2 secondes',
        estimatedGain: '+150% de conversions',
        estimatedCost: '2 500€ - 5 000€'
      });
    }

    // Axe 2: Sécurité
    if (websiteAnalysis.security.securityScore < 80) {
      axes.push({
        id: 'security',
        title: '🔒 Sécurité et Confiance',
        priority: 'CRITIQUE',
        impact: 'Élevé',
        description: 'Votre site présente des vulnérabilités de sécurité qui peuvent compromettre vos données',
        problems: [
          'Pas de certificat SSL/HTTPS',
          'Headers de sécurité manquants',
          'Vulnérabilités détectées',
          'Données sensibles exposées'
        ],
        businessImpact: 'Risque de piratage et perte de confiance clients',
        solution: 'Sécurisation complète avec certificat SSL et protection avancée',
        estimatedGain: 'Confiance client + protection légale',
        estimatedCost: '1 500€ - 3 000€'
      });
    }

    // Axe 3: SEO et Visibilité
    if (websiteAnalysis.seo.seoScore < 70) {
      axes.push({
        id: 'seo',
        title: '📈 SEO et Visibilité Google',
        priority: 'HAUTE',
        impact: 'Élevé',
        description: 'Votre site n\'apparaît pas dans les premiers résultats Google, vous perdez 90% du trafic',
        problems: [
          'Meta descriptions manquantes',
          'Structure des titres incorrecte',
          'Pas de sitemap XML',
          'URLs non optimisées',
          'Balises alt manquantes'
        ],
        businessImpact: 'Perte de 90% du trafic organique potentiel',
        solution: 'Optimisation SEO complète pour apparaître en première page',
        estimatedGain: '+300% de trafic organique',
        estimatedCost: '3 000€ - 6 000€'
      });
    }

    // Axe 4: Mobile
    if (websiteAnalysis.mobile.mobileScore < 75) {
      axes.push({
        id: 'mobile',
        title: '📱 Expérience Mobile',
        priority: 'HAUTE',
        impact: 'Élevé',
        description: '65% de vos visiteurs sont sur mobile, mais votre site n\'est pas adapté',
        problems: [
          'Site non responsive',
          'Boutons trop petits',
          'Navigation difficile',
          'Texte illisible'
        ],
        businessImpact: 'Perte de 65% des visiteurs mobiles',
        solution: 'Design responsive professionnel',
        estimatedGain: '+200% d\'engagement mobile',
        estimatedCost: '4 000€ - 8 000€'
      });
    }

    // Axe 5: E-commerce (si applicable)
    if (websiteAnalysis.ecommerce && websiteAnalysis.ecommerce.ecommerceScore < 70) {
      axes.push({
        id: 'ecommerce',
        title: '🛒 Ventes en Ligne',
        priority: 'HAUTE',
        impact: 'Élevé',
        description: 'Votre boutique en ligne ne convertit pas, vous perdez des ventes',
        problems: [
          'Processus d\'achat compliqué',
          'Pas de moyens de paiement sécurisés',
          'Fiches produits incomplètes',
          'Panier abandonné'
        ],
        businessImpact: 'Taux de conversion < 2% (norme: 3-5%)',
        solution: 'Optimisation du tunnel de vente',
        estimatedGain: '+150% de conversions',
        estimatedCost: '5 000€ - 10 000€'
      });
    }

    // Axe 6: Accessibilité
    if (websiteAnalysis.accessibility && websiteAnalysis.accessibility.accessibilityScore < 70) {
      axes.push({
        id: 'accessibility',
        title: '♿ Accessibilité',
        priority: 'MOYENNE',
        impact: 'Modéré',
        description: 'Votre site n\'est pas accessible aux personnes en situation de handicap',
        problems: [
          'Pas de navigation au clavier',
          'Contraste insuffisant',
          'Images sans alt text',
          'Structure sémantique incorrecte'
        ],
        businessImpact: 'Exclusion de 15% de la population',
        solution: 'Mise en conformité RGAA',
        estimatedGain: 'Audience élargie + conformité légale',
        estimatedCost: '2 000€ - 4 000€'
      });
    }

    return axes;
  };

  // Génération du texte accrocheur
  const generateHookText = () => {
    const criticalIssues = generateImprovementAxes().filter(axe => axe.priority === 'CRITIQUE').length;
    const totalIssues = generateImprovementAxes().length;
    
    return {
      title: `🚨 AUDIT GRATUIT - ${business.name}`,
      subtitle: `${criticalIssues} problème${criticalIssues > 1 ? 's' : ''} critique${criticalIssues > 1 ? 's' : ''} détecté${criticalIssues > 1 ? 's' : ''} sur votre site web`,
      hook: `Bonjour ${business.name},

J'ai analysé votre site web ${business.website} et j'ai identifié ${totalIssues} axes d'amélioration majeurs qui impactent directement votre business.

${criticalIssues > 0 ? `🚨 ${criticalIssues} problème${criticalIssues > 1 ? 's' : ''} critique${criticalIssues > 1 ? 's' : ''} : Votre site perd actuellement des clients et de l'argent.` : ''}

📊 Mon analyse révèle que vous pourriez augmenter vos conversions de +150% à +300% en corrigeant ces points.

🎯 Je vous propose un audit détaillé gratuit pour identifier précisément les actions à mettre en place.

Êtes-vous disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]`,
      emailSubject: `Audit gratuit - ${criticalIssues} problème${criticalIssues > 1 ? 's' : ''} critique${criticalIssues > 1 ? 's' : ''} détecté${criticalIssues > 1 ? 's' : ''} sur ${business.name}`,
      emailBody: `Bonjour,

Suite à l'analyse de votre site web ${business.website}, j'ai identifié ${totalIssues} axes d'amélioration majeurs qui impactent votre business.

${criticalIssues > 0 ? `🚨 ${criticalIssues} problème${criticalIssues > 1 ? 's' : ''} critique${criticalIssues > 1 ? 's' : ''} nécessitent une action immédiate.` : ''}

📈 Potentiel d'amélioration : +150% à +300% de conversions

Je vous propose un audit détaillé gratuit pour :
• Identifier précisément les problèmes
• Prioriser les actions
• Estimer les gains potentiels
• Proposer un plan d'action

Disponible pour un échange de 15 minutes cette semaine ?

Cordialement,
[Votre nom]
[Votre téléphone]`
    };
  };

  // Génération du rapport d'audit
  const generateAuditReport = () => {
    const axes = generateImprovementAxes();
    const hook = generateHookText();
    
    return {
      businessInfo: {
        name: business.name,
        website: business.website,
        address: business.address,
        phone: business.phone,
        email: business.email
      },
      summary: {
        totalIssues: axes.length,
        criticalIssues: axes.filter(axe => axe.priority === 'CRITIQUE').length,
        highPriorityIssues: axes.filter(axe => axe.priority === 'HAUTE').length,
        estimatedTotalCost: axes.reduce((sum, axe) => {
          const cost = parseInt(axe.estimatedCost.split('€')[0].replace(/\s/g, ''));
          return sum + cost;
        }, 0),
        estimatedTotalGain: axes.reduce((sum, axe) => {
          const gain = parseInt(axe.estimatedGain.split('%')[0].replace(/\s/g, ''));
          return sum + gain;
        }, 0) / axes.length
      },
      axes,
      hook,
      recommendations: {
        immediate: axes.filter(axe => axe.priority === 'CRITIQUE'),
        shortTerm: axes.filter(axe => axe.priority === 'HAUTE'),
        longTerm: axes.filter(axe => axe.priority === 'MOYENNE')
      }
    };
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const downloadAudit = () => {
    const report = generateAuditReport();
    const content = `AUDIT GRATUIT - ${report.businessInfo.name}

INFORMATIONS ENTREPRISE
Nom: ${report.businessInfo.name}
Site web: ${report.businessInfo.website}
Adresse: ${report.businessInfo.address}
Téléphone: ${report.businessInfo.phone}
Email: ${report.businessInfo.email}

RÉSUMÉ EXÉCUTIF
- Total des problèmes identifiés: ${report.summary.totalIssues}
- Problèmes critiques: ${report.summary.criticalIssues}
- Problèmes haute priorité: ${report.summary.highPriorityIssues}
- Coût total estimé: ${report.summary.estimatedTotalCost}€
- Gain potentiel moyen: +${report.summary.estimatedTotalGain}%

AXES D'AMÉLIORATION

${report.axes.map(axe => `
${axe.title}
Priorité: ${axe.priority}
Impact: ${axe.impact}
Description: ${axe.description}

Problèmes identifiés:
${axe.problems.map(p => `• ${p}`).join('\n')}

Impact business: ${axe.businessImpact}
Solution proposée: ${axe.solution}
Gain estimé: ${axe.estimatedGain}
Coût estimé: ${axe.estimatedCost}

`).join('\n')}

TEXTE D'ACCROCHE

${report.hook.hook}

RECOMMANDATIONS

Actions immédiates (Critiques):
${report.recommendations.immediate.map(axe => `• ${axe.title}`).join('\n')}

Actions court terme (Haute priorité):
${report.recommendations.shortTerm.map(axe => `• ${axe.title}`).join('\n')}

Actions long terme (Moyenne priorité):
${report.recommendations.longTerm.map(axe => `• ${axe.title}`).join('\n')}
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-${business.name.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const report = generateAuditReport();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="primary" />
        Générateur d'Audit Automatique
      </Typography>

      {/* Résumé exécutif */}
      <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'white' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 Résumé Exécutif - {business.name}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="error.light">
                {report.summary.criticalIssues}
              </Typography>
              <Typography variant="body2">Problèmes Critiques</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="warning.light">
                {report.summary.highPriorityIssues}
              </Typography>
              <Typography variant="body2">Haute Priorité</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="success.light">
                {report.summary.estimatedTotalCost}€
              </Typography>
              <Typography variant="body2">Budget Estimé</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h4" color="info.light">
                +{report.summary.estimatedTotalGain}%
              </Typography>
              <Typography variant="body2">Gain Potentiel</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Axes d'amélioration */}
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingIcon color="primary" />
        Axes d'Amélioration Identifiés
      </Typography>

      {report.axes.map((axe, index) => (
        <Accordion 
          key={axe.id}
          expanded={expandedSection === axe.id}
          onChange={() => setExpandedSection(expandedSection === axe.id ? false : axe.id)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {axe.title}
              </Typography>
              <Chip 
                label={axe.priority} 
                color={axe.priority === 'CRITIQUE' ? 'error' : axe.priority === 'HAUTE' ? 'warning' : 'default'}
                size="small"
              />
              <Chip 
                label={axe.impact} 
                variant="outlined" 
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  🚨 Problèmes Identifiés
                </Typography>
                <List dense>
                  {axe.problems.map((problem, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText primary={problem} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  💼 Impact Business
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {axe.businessImpact}
                </Alert>
                
                <Typography variant="subtitle1" color="success.main" gutterBottom>
                  ✅ Solution Proposée
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {axe.solution}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip 
                    icon={<TrendingIcon />} 
                    label={axe.estimatedGain} 
                    color="success" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<MoneyIcon />} 
                    label={axe.estimatedCost} 
                    color="info" 
                    variant="outlined"
                  />
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Texte d'accroche */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Texte d'Accroche pour Prospect
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Sujet Email:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ flexGrow: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                {report.hook.emailSubject}
              </Typography>
              <Tooltip title="Copier le sujet">
                <IconButton 
                  onClick={() => copyToClipboard(report.hook.emailSubject, 'sujet')}
                  color={copiedText === 'sujet' ? 'success' : 'primary'}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Corps du message:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="body1" sx={{ flexGrow: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1, whiteSpace: 'pre-line' }}>
                {report.hook.emailBody}
              </Typography>
              <Tooltip title="Copier le message">
                <IconButton 
                  onClick={() => copyToClipboard(report.hook.emailBody, 'message')}
                  color={copiedText === 'message' ? 'success' : 'primary'}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={downloadAudit}
          size="large"
        >
          Télécharger l'Audit Complet
        </Button>
      </Box>

      {copiedText && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {copiedText === 'sujet' ? 'Sujet copié !' : 'Message copié !'}
        </Alert>
      )}
    </Box>
  );
};

export default AuditGenerator;